import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import type { ListingIntentFilter } from "./context/listing-browse-types";

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export const LISTING_BROWSE_PAGE_SIZE = 60;

export interface UseListingBrowseProductsArgs {
  location: {
    lat: number;
    lng: number;
    postalCode?: string;
    state?: string;
  } | null;
  radiusMiles: number;
  quantity: number;
  productType: string | null;
  conditions: string[];
  priceRange: { min: number | null; max: number | null };
  specFilters: Array<{ field: string; value: string }>;
  stateCode: string | null;
  listedAfterDays: number | null;
  intentFilter: ListingIntentFilter;
  searchQuery: string;
  saleOnly?: boolean;
  initialListings?: BrowseListing[];
  initialTotalCount?: number;
}

export type FilterKey = {
  location: [number | undefined, number | undefined, string | undefined];
  productType: string | null;
  radiusMiles: number;
  conditions: string[];
  priceRange: [number | null, number | null];
  specFilters: Array<{ field: string; value: string }>;
  stateCode: string | null;
  listedAfterDays: number | null;
  intentFilter: ListingIntentFilter;
  searchQuery: string;
  quantity: number;
};

export function buildFilterKey(args: UseListingBrowseProductsArgs): FilterKey {
  return {
    location: [
      args.location?.lat,
      args.location?.lng,
      args.location?.postalCode,
    ],
    productType: args.productType,
    radiusMiles: args.radiusMiles,
    conditions: args.conditions,
    priceRange: [args.priceRange.min, args.priceRange.max],
    specFilters: args.specFilters,
    stateCode: args.stateCode,
    listedAfterDays: args.listedAfterDays,
    intentFilter: args.intentFilter,
    searchQuery: args.searchQuery,
    quantity: args.quantity,
  };
}

export function filterKeysEqual(first: FilterKey, second: FilterKey): boolean {
  return JSON.stringify(first) === JSON.stringify(second);
}

export function getListedAfterTimestamp(
  listedAfterDays: number | null,
  now: number,
): number | undefined {
  return listedAfterDays !== null && listedAfterDays > 0
    ? now - listedAfterDays * MILLISECONDS_PER_DAY
    : undefined;
}

export function shouldLoadMoreSearchResults(args: {
  hasSearch: boolean;
  status: string;
  resultCount: number;
  requiredResultCount: number;
}): boolean {
  return (
    args.hasSearch &&
    args.status === "CanLoadMore" &&
    args.resultCount < args.requiredResultCount
  );
}

export function getSettledSearchPage(args: {
  hasSearch: boolean;
  status: string;
  resultCount: number;
  currentPage: number;
  pageSize: number;
}): number {
  const searchIsSettled = args.status === "Exhausted";
  if (!args.hasSearch || !searchIsSettled) {
    return args.currentPage;
  }

  const finalPage = Math.max(1, Math.ceil(args.resultCount / args.pageSize));
  return Math.min(args.currentPage, finalPage);
}
